import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Event, LIVE_RESULTS_STATUS, REGISTRATION_STATUS } from '@root/src/types';
import { formatEventDate } from '@root/src/utils/dates';
import useEventInfo from '@root/src/hooks/useEventInfo';
import { RESPONSIVE, useResponsive } from '@services/responsiveProvider';
import getRoute, { QUERY_ENUM, routeRegex, useTestRoute } from '@services/routeList';
import MobileTabs from '@features/ui/components/Tabs/Mobile';
import DesktopTabs from '@features/ui/components/Tabs/Desktop';
import Card from '@features/ui/components/Card';
import { selectEventDetail } from '@features/eventDetail/slices/eventDetailServerSlice';
import EventTabLiveTiming from '@features/eventDetail/components/EventTabLiveTiming';
import { getQueryEventId } from '@features/eventDetail/utils';

const computeRegistrationStatus = (event: Event) => (
  event.quotas?.full
    ? REGISTRATION_STATUS.CLOSED
    : event.status.registration
);

const EventTabs = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const event = useSelector(selectEventDetail(getQueryEventId(router.query)));
  const eventSlug = router.query[QUERY_ENUM.eventSlug] as string;
  const routeTester = useTestRoute();
  const responsive = useResponsive();
  const {
    hasLiveTiming,
    isLiveTimingNow,
    liveTimingUrl,
    hasEngagedList,
    isSeries,
    hasResults,
    hasRankedSeries,
    hasLiveResults,
  } = useEventInfo(event);

  const subscriptionSubTitle = t(`eventTabs_tabSubTitleSubscriptions_${computeRegistrationStatus(event)}.message`);

  let liveResultsSubTitle: string | React.ReactElement = '';
  if (hasLiveResults === LIVE_RESULTS_STATUS.ONLINE) {
    liveResultsSubTitle = <EventTabLiveTiming />;
  } else if (hasLiveResults === LIVE_RESULTS_STATUS.OFFLINE) {
    liveResultsSubTitle = t('eventTabs_tabTitleResultsOpen.message');
  }

  const showStartListFrom = event.showStartListFrom ? formatEventDate(event.showStartListFrom) : '-';
  const tabs = [
    {
      key: 'description',
      title: t('eventTabs_tabTitleDescription.message'),
      href: getRoute.events(eventSlug).description(),
      routeTest: routeRegex.eventDescription,
      qaId: 'description',
    },
    {
      key: 'subscriptions',
      title: t('eventTabs_tabTitleSubscriptions.message'),
      subTitle: subscriptionSubTitle,
      href: getRoute.events(eventSlug).subscriptions(),
      routeTest: routeRegex.eventSubscription,
      qaId: 'subscriptions',
    },
    {
      key: 'engaged',
      title: t('eventTabs_tabTitleEngaged.message'),
      disabled: !hasEngagedList,
      subTitle: hasEngagedList ? t('eventTabs_tabTitleEngagedOpen.message') : showStartListFrom,
      href: getRoute.events(eventSlug).engaged(),
      routeTest: routeRegex.eventEngaged,
      qaId: 'engaged',
    },
    (hasLiveTiming && {
      key: 'liveTiming',
      title: t('eventTabs_tabTitleLiveTiming.message'),
      disabled: !isLiveTimingNow,
      subTitle: isLiveTimingNow && <EventTabLiveTiming />,
      href: liveTimingUrl,
      routeTest: null,
      qaId: 'liveTiming',
    }),
    (hasLiveResults === LIVE_RESULTS_STATUS.NO ? ({
      key: 'results',
      title: t('eventTabs_tabTitleResults.message'),
      href: getRoute.events(eventSlug).results(),
      disabled: !hasResults,
      subTitle: hasResults ? t('eventTabs_tabTitleResultsOpen.message') : '',
      routeTest: routeRegex.eventResults,
      qaId: 'results',
    }) : ({
      key: 'liveResults',
      title: t('eventTabs_tabTitleResults.message'),
      href: getRoute.events(eventSlug).liveResults(),
      disabled: hasLiveResults === LIVE_RESULTS_STATUS.EMPTY,
      subTitle: liveResultsSubTitle,
      routeTest: routeRegex.eventLiveResults,
      qaId: 'liveResults',
    })),
    (isSeries && {
      key: 'serieResults',
      title: t('eventTabs_tabTitleRanking.message'),
      href: getRoute.events(eventSlug).series(),
      disabled: !hasRankedSeries,
      subTitle: hasRankedSeries ? t('eventTabs_tabTitleRankingOpen.message') : '',
      routeTest: routeRegex.eventSeries,
      qaId: 'ranking',
    }),
  ].filter(Boolean);

  const currentIndex = tabs.findIndex(tab => tab.routeTest && routeTester(tab.routeTest));

  if (responsive === RESPONSIVE.DESKTOP) {
    return (
      <DesktopTabsWrapper>
        <DesktopBar />
        <DesktopTabs tabs={tabs} currentIndex={currentIndex >= 0 ? currentIndex : 0} />
      </DesktopTabsWrapper>
    );
  }

  return <StyledMobileTabs tabs={tabs} currentIndex={currentIndex >= 0 ? currentIndex : 0} />;
};

const StyledMobileTabs = styled(MobileTabs)`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const DesktopTabsWrapper = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const DesktopBar = styled(Card)`
  position: absolute;
  height: 100%;
  top: 0;
  /* magic trick */
  width: 200vw;
  left: -100vw;
`;

export default EventTabs;
